/**
 * @file watchers/orderSessionWatcher.ts
 *
 * Recreates the guest cart when the user's session ends (logout or expiry).
 */
'use client';

// 3rd Party Imports
import { useSession } from 'next-auth/react';
import { useEffect, useRef } from 'react';

// Context Imports
import { useOrder } from '@/lib/context/orderContext';

// Function Imports
import { recreateCartWithItems } from '@/lib/functions/recreateCartWithItems';

export default function OrderSessionWatcher() {
  const { status } = useSession();
  const hadAuthenticatedSession = useRef(false);
  const authenticatedCartToken = useRef<string | null>(null);
  const { state: orderState, dispatch: orderDispatch } = useOrder();

  useEffect(() => {
    if (status === 'authenticated') {
      hadAuthenticatedSession.current = true;
      authenticatedCartToken.current = orderState.token;
      return;
    }

    if (status !== 'unauthenticated' || !hadAuthenticatedSession.current) {
      return;
    }

    hadAuthenticatedSession.current = false;

    const tokenToRecreate = authenticatedCartToken.current;
    authenticatedCartToken.current = null;

    if (!tokenToRecreate) {
      return;
    }

    // Log Out link already rotated the cart before signOut().
    if (orderState.token && orderState.token !== tokenToRecreate) {
      return;
    }

    void recreateCartWithItems({ ...orderState, token: tokenToRecreate }, orderDispatch).catch((err) => {
      console.error('Cart recreation failed on logout:', err);
    });
  }, [status, orderState, orderDispatch]);

  return null;
}
